import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import StepHeader from './StepHeader';
import CtaButton from './CtaButton';
import {
  PlayerPickerProvider,
  usePlayerPicker
} from '@components/manual-team-entry/player-picker-provider';
import PlayerRow, {
  PITCH_XI_ROWS,
  PITCH_BENCH_ROW
} from '@components/manual-team-entry/player-row';
import PitchShell from '@components/manual-team-entry/pitch-shell';
import type { TeamMember } from '@components/manual-team-entry/types';
import type { SanityAccentHeadline } from '@lib/sanity/types';
import { fetchSquadPlayers } from '@lib/api/gytr-portraits';
import {
  assessScreenshotSquad,
  countSlotShortfall,
  describeMatchPosition,
  describeShortfall,
  FULL_SQUAD_SIZE,
  type ScreenshotMatch
} from '@lib/quiz/screenshot-squad';

type ImportReviewStepProps = {
  /** The screenshot read to review, straight off the import endpoint. */
  matches: ScreenshotMatch[];
  /** Copy for the step, so each funnel keeps its own voice. */
  heading?: string;
  /** A Sanity-authored heading, which takes precedence over `heading`. */
  accentHeading?: SanityAccentHeadline | null;
  subtext?: string;
  continueLabel?: string;
  backLabel?: string;
  isSubmitting?: boolean;
  /** A failure from the caller's own submit, rendered under the pitch. */
  submitError?: string | null;
  /** The confirmed squad, in builder slot order (starting XI then bench). */
  onConfirm: (team: TeamMember[]) => void;
  onBack: () => void;
};

const HYDRATE_ERROR_MESSAGE =
  "We couldn't load the players we read from your screenshot. Pick your squad below to carry on.";

const emptyTeam = (): TeamMember[] =>
  Array.from({ length: FULL_SQUAD_SIZE }, (_, playerId) => ({
    playerId,
    player: null
  }));

const ImportReviewInner: FC<ImportReviewStepProps> = ({
  matches,
  heading,
  accentHeading,
  subtext,
  continueLabel,
  backLabel,
  isSubmitting,
  submitError,
  onConfirm,
  onBack
}) => {
  const { closeSlot } = usePlayerPicker();
  const [team, setTeam] = useState<TeamMember[]>(emptyTeam);
  const [isHydrating, setIsHydrating] = useState(true);
  const [hydrateError, setHydrateError] = useState<string | null>(null);

  const assessment = useMemo(() => assessScreenshotSquad(matches), [matches]);

  // The import endpoint sends enough of each player to name them, but the
  // builder's slots hold the full player record the picker produces, so the
  // read has to be resolved through the players endpoint before it can be
  // placed. An id it does not know about stays an empty slot: better a gap the
  // visitor can fill than a player who cannot be edited.
  useEffect(() => {
    let isCurrent = true;
    const placedIds = assessment.placements
      .map((placed) => placed?.player.id)
      .filter((id): id is string => Boolean(id));

    setIsHydrating(true);
    setHydrateError(null);

    fetchSquadPlayers(placedIds)
      .then((playersById) => {
        if (!isCurrent) return;
        setTeam(
          assessment.placements.map((placed, playerId) => ({
            playerId,
            player: placed ? playersById.get(placed.player.id) ?? null : null
          }))
        );
      })
      .catch(() => {
        if (!isCurrent) return;
        setTeam(emptyTeam());
        setHydrateError(HYDRATE_ERROR_MESSAGE);
      })
      .finally(() => {
        if (isCurrent) setIsHydrating(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [assessment]);

  const handleDelete = useCallback(
    (playerId: number) => {
      setTeam((previous) =>
        previous.map((member) =>
          member.playerId === playerId ? { playerId, player: null } : member
        )
      );
      closeSlot();
    },
    [closeSlot]
  );

  const shortfall = countSlotShortfall(team);
  const outstanding = describeShortfall(shortfall);
  const isComplete = shortfall.length === 0;

  const handleContinue = useCallback(() => {
    if (!isComplete || isSubmitting) return;
    onConfirm(team);
  }, [isComplete, isSubmitting, onConfirm, team]);

  // Named rather than counted: a player disappearing from their own squad with
  // no explanation is the one outcome the visitor cannot act on.
  const droppedNames = assessment.dropped.map(
    (dropped) =>
      `${dropped.player.displayName} (read as a ${describeMatchPosition(
        dropped
      )})`
  );

  const continueText = isSubmitting
    ? 'Checking your squad…'
    : outstanding
    ? `Add ${outstanding}`
    : continueLabel ?? 'Continue';

  return (
    <section
      className="flex flex-col items-center px-4 pb-16 pt-6"
      onClick={() => closeSlot()}
      data-cy="import-review-step"
    >
      <StepHeader
        heading={heading ?? 'Check your squad'}
        accentHeading={accentHeading}
        subtext={
          subtext ??
          'We read your team from your screenshot, but a few players need a second look. Fill the empty spots and carry on.'
        }
      />

      {droppedNames.length > 0 && (
        <div
          className="mt-4 w-full max-w-sm rounded-lg bg-white px-4 py-3 shadow-100"
          data-cy="import-review-dropped"
        >
          <p className="font-heading text-xs font-bold uppercase tracking-widest text-primary-500">
            We couldn&apos;t place
          </p>
          <ul className="mt-1 font-body text-sm text-neutral-600">
            {droppedNames.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </ul>
        </div>
      )}

      {hydrateError && (
        <p className="mt-4 w-full max-w-sm text-center font-body text-sm text-danger-600">
          {hydrateError}
        </p>
      )}

      {isHydrating ? (
        <p className="mt-10 font-body text-sm text-neutral-500">
          Loading your squad…
        </p>
      ) : (
        <PitchShell
          xi={PITCH_XI_ROWS.map((row) => (
            <PlayerRow
              key={`import-review-row-${row.positionId}`}
              row={row}
              team={team}
              setTeam={setTeam}
              onDelete={handleDelete}
            />
          ))}
          bench={
            <PlayerRow
              row={PITCH_BENCH_ROW}
              team={team}
              setTeam={setTeam}
              onDelete={handleDelete}
            />
          }
        />
      )}

      {submitError && (
        <p
          className="mt-4 w-full max-w-sm text-center font-body text-sm text-danger-600"
          data-cy="import-review-error"
        >
          {submitError}
        </p>
      )}

      <div className="mt-6 w-full max-w-sm space-y-3">
        <CtaButton
          onClick={handleContinue}
          disabled={!isComplete || isSubmitting || isHydrating}
          dataCy="import-review-continue"
        >
          {continueText}
        </CtaButton>
        <button
          type="button"
          onClick={onBack}
          data-cy="import-review-back"
          className="block w-full text-center font-body text-sm font-bold text-primary-500 transition-colors hover:text-primary-600"
        >
          {backLabel ?? 'Start again'}
        </button>
      </div>
    </section>
  );
};

/**
 * The review pitch a best-effort screenshot import lands on when the read is
 * not a legal squad (FFH-2738).
 *
 * Deliberately has no budget line. An imported squad is a real team whose
 * players have drifted in price, so it routinely sits above the £100m a new
 * team gets, and gating this on the builder's budget would swap one dead end
 * for another.
 */
const ImportReviewStep: FC<ImportReviewStepProps> = (props) => (
  <PlayerPickerProvider>
    <ImportReviewInner {...props} />
  </PlayerPickerProvider>
);

export default ImportReviewStep;
